import * as React from "react";
import { useLocation } from "react-router-dom";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { currentYear, parseCurrencyToNumber } from "../../utils/schema";
import { ViewContext } from "components/lib";
import { fetchCompanyTrailers } from "./api";
import { TrailerListItem } from "./TrailerListItem";
import { TrailerUpsertDialog } from "./TrailerUpsertDialog";
import { ExistingTrailersPicker } from "./ExistingTrailersPicker";
import { Button } from "../../../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../../../components/ui/dialog";
import { Plus } from "lucide-react";
import { FieldError } from "../FieldError";
import { useEffect } from "react";

const arraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export function Trailers() {
  const { control, setValue, getValues, watch, trigger } = useFormContext();
  const { fields, append, update, remove, replace } = useFieldArray({
    control,
    name: "step7.trailers",
    keyName: "fieldId",
  });
  const step1Carrier = useWatch({ control, name: "step1.carrier" });
  const step7Carrier = useWatch({ control, name: "step7.carrier" });

  const context = React.useContext(ViewContext);
  const location = useLocation();
  const companyId = location?.state?.data?.company_id;
  const quoteId = location?.state?.data?.quote_id;

  const [openChooser, setOpenChooser] = React.useState(false);
  const [openSelect, setOpenSelect] = React.useState(false);
  const [upsertOpen, setUpsertOpen] = React.useState(false);
  const [mode, setMode] = React.useState("create");
  const [editingIndex, setEditingIndex] = React.useState(null);

  const [existingTrailers, setExistingTrailers] = React.useState([]);

  // load company trailers (when not editing a specific quote)
  React.useEffect(() => {
    if (companyId) {
      fetchCompanyTrailers(companyId)
        .then((rows) => {
          const items = rows.map((t) => ({
            id: t.id,
            trailerId: t.id,
            type: "trailer",
            vin: t.vin,
            model: t.model,
            year: t.year,
            make: t.make,
            bodyType: t.bodytype,
            value: String(t.stated_value ?? "").replaceAll(/\D/g, ""),
            deductibleType: "FollowsPolicy",
            deductible: "1500",
          }));
          if (!quoteId) {
            const totalValue = items.reduce((acc, it) => acc + Number(it.value || 0), 0);
            replace(items);
            setValue("step7.nbrOfTrailers", items.length, { shouldDirty: false });
            setValue("step7.valueOfTrailers", totalValue, { shouldDirty: false });
          }
          setExistingTrailers(items);
        })
        .catch((err) => {
          console.error(err);
          context.notification?.show?.("Error on load company vehicles", "error", true);
        });
    }
  }, [companyId, quoteId, replace, setValue, context.notification]);

  // Keep totals up-to-date
  const trailers = watch("step7.trailers");
  React.useEffect(() => {
    const n = trailers?.length ?? 0;
    const total = (trailers ?? []).reduce((acc, v) => acc + Number(parseCurrencyToNumber(v.value)), 0);
    setValue("step7.nbrOfTrailers", n, { shouldDirty: false });
    setValue("step7.valueOfTrailers", total, { shouldDirty: false });
  }, [trailers, setValue]);

  // Mirror carriers into step7 for validation
  useEffect(() => {
    const list = Array.isArray(step1Carrier) ? step1Carrier : step1Carrier ? [step1Carrier] : [];
    const normalized = list.map((s) => s ?? "");
    if (!arraysEqual(normalized, step7Carrier ?? [])) {
      setValue("step7.carrier", normalized, { shouldDirty: false, shouldValidate: true });
    }
  }, [step1Carrier, step7Carrier, setValue]);

  // Attach an inline age message for read-only list (visual parity)
  const withAgeMessage = (t) => {
    const age = currentYear - Number(t.year);
    const hasCoverWhale = (step7Carrier ?? []).includes("Cover Whale");
    const hasNirvana = (step7Carrier ?? []).includes("Nirvana");
    const hasProgressive = (step7Carrier ?? []).includes("Progressive");

    if (!hasCoverWhale && !hasNirvana) return t; // Progressive-only or other carriers: no age warning

    if (age > 23) {
      const message = hasProgressive
        ? "Cover Whale/Nirvana do not accept trailers above 23 years of age. Continuing will quote Progressive only."
        : "Trailers above 23 years of age are not eligible";

      return { ...t, _ageError: message };
    }

    return t;
  };

  const openCreate = () => { setMode("create"); setEditingIndex(null); setUpsertOpen(true); };
  const openEdit = (idx) => { setMode("edit"); setEditingIndex(idx); setUpsertOpen(true); };

  const onUpsertDone = (trailer) => {
    if (mode === "create") append(trailer);
    else if (editingIndex != null) update(editingIndex, trailer);
  };

  useEffect(() => {
    if (fields.length > 0) {
      trigger("step7.trailers")
    }
  }, [fields]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">Trailers</h2>
        <Button
          className="bg-red-600 hover:bg-red-700 text-white"
          onClick={() => setOpenChooser(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Trailer
        </Button>
      </div>

      {/* Read-only list */}
      <div className="px-3 space-y-3">
        {fields.length > 0 && (
          <div className="space-y-3">
            {fields.map((row, index) => {
              const t = withAgeMessage(trailers[index]);
              return (
                <TrailerListItem
                  key={row.fieldId}
                  trailer={t}
                  index={index}
                  onEdit={() => openEdit(index)}
                  onDelete={() => remove(index)}
                />
              );
            })}
          </div>
        )}
        <FieldError step={"step7"} name="step7.trailers" />
      </div>

      {/* Chooser */}
      <Dialog open={openChooser} onOpenChange={setOpenChooser}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              Create a new trailer or choose from your existing trailers?
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <Button variant="outline" onClick={() => { setOpenChooser(false); setOpenSelect(true); }}>
              Select Existing
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { setOpenChooser(false); openCreate(); }}>
              Create New Trailer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Select Existing */}
      <Dialog open={openSelect} onOpenChange={setOpenSelect}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Select Existing Trailer</DialogTitle></DialogHeader>
          <ExistingTrailersPicker
            existing={existingTrailers}
            selected={trailers ?? []}
            onPick={(t) => {
              const cur = getValues("step7.trailers") || [];
              if (cur.some((x) => x.vin === t.vin)) {
                context.notification?.show?.("Trailer already added.", "error", true);
                return;
              }
              append(t);
              setOpenSelect(false);
            }}
          />
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit dialog */}
      <TrailerUpsertDialog
        open={upsertOpen}
        onOpenChange={setUpsertOpen}
        mode={mode}
        initial={editingIndex != null ? trailers[editingIndex] : undefined}
        companyId={companyId}
        quoteId={quoteId}
        getWholeQuoteData={() => getValues()} // send full form payload to backend when quoteId exists
        onDone={onUpsertDone}
      />
    </div>
  );
}
